import { AlertCircle, Clock, RadioTower } from 'lucide-react-native';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '@/components/Text';
import { SUPPORT_EMAIL } from '@/constants/api';
import { colors, radii, spacing } from '@/constants/theme';
import { openEmail } from '@/lib/openEmail';
import { formatRelativeDated } from '@/lib/relativeTime';
import type { Action, Deal, StatusCard } from '@/types/deal';

type LastReportedProps = {
  deal: Deal;
  action?: Action;
  /** Injectable clock for tests. */
  now?: number;
  testID?: string;
};

/**
 * Deal Steps v2. The tracker strip between the deal description and the
 * stepper: a soft green box with an antenna icon that answers "did it
 * track?" in one glance, so a user who never reads the steps still gets
 * the answer. Mirrors the Deal Step Tracker brainstorm (Sep 2).
 *
 *   [antenna]  SoFi confirmed: Invest account approved
 *              4m ago
 *   [antenna]  Nothing confirmed by SoFi yet          (neutral tint)
 *
 * Reads `action.last_provider_event` (the latest DIRECT confirmation). The
 * step name is the server's (`step_name`, resolved against the deal's
 * current config, and attributed to the last user step when the postback
 * landed on a provider-only step such as "Claim your $50"). The strip only
 * ever shows a report whose step name resolved, never a raw event name and
 * never an empty "nothing yet" line: with nothing to say it takes no room.
 */
export function LastReported({ deal, action, now, testID = 'last-reported' }: LastReportedProps) {
  // Server-authored card (Deal Steps v2 final shape): what the brand confirmed
  // and the one next action, with its tone. Rendered for every deal when
  // present. The strip below is the fallback for a server without it.
  const card = action?.status_card;
  if (card) return <StatusCardView card={card} testID={testID} />;

  const last = action?.last_provider_event;
  const stepName = last?.step_name;
  if (!last || !stepName) return null;

  // On its own line, so the capitalised, normally-spaced form.
  const time = formatRelativeDated(last.at, now);

  return (
    <View style={styles.box} testID={testID}>
      <RadioTower size={16} color={colors.accentText} strokeWidth={2} style={styles.icon} />
      <View style={styles.text}>
        <Text variant="caption" color="textMuted">
          {`${deal.brand_name} confirmed: `}
          <Text variant="captionBold" color="deepGreen">
            {stepName}
          </Text>
        </Text>
        {time ? (
          <Text variant="caption" color="textMuted" style={styles.time}>
            {time}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function StatusCardView({ card, testID }: { card: StatusCard; testID: string }) {
  const tone: Tone = TONES[card.tone] ?? NEUTRAL_TONE;
  const Icon = tone.icon;
  return (
    <View style={[styles.box, tone.box]} testID={testID} accessibilityRole="summary">
      <Icon size={16} color={tone.iconColor} strokeWidth={2} style={styles.icon} />
      <View style={styles.text}>
        <Text variant="captionBold" style={tone.title}>
          {card.title}
        </Text>
        {card.body ? (
          <Text variant="caption" color="textMuted" style={styles.time}>
            {card.body}
          </Text>
        ) : null}
        {card.support_email ? (
          <Pressable
            onPress={() => {
              const mail = card.support_email;
              if (mail)
                void openEmail(mail.to || SUPPORT_EMAIL, {
                  subject: mail.subject,
                  body: mail.body,
                });
            }}
            hitSlop={8}
            accessibilityRole="link"
            accessibilityLabel="Email support about this step"
            style={styles.supportLink}
            testID={`${testID}-support`}
          >
            <Text variant="captionBold" style={tone.title}>
              Still stuck? Contact support
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  box: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: radii.base,
    backgroundColor: colors.paidPillBg,
  },
  boxEmpty: {
    backgroundColor: colors.neutralPillBg,
  },
  icon: {
    marginTop: 1,
  },
  text: {
    flex: 1,
  },
  time: {
    marginTop: 1,
  },
  supportLink: {
    alignSelf: 'flex-start',
    marginTop: spacing.xs,
    paddingVertical: spacing.xs,
  },
  boxWarning: {
    backgroundColor: colors.warnPillBg,
  },
  boxDanger: {
    backgroundColor: colors.overduePillBg,
  },
  titleNeutral: {
    color: colors.text,
  },
  titleSuccess: {
    color: colors.deepGreen,
  },
  titleWarning: {
    color: colors.warnPillText,
  },
  titleDanger: {
    color: colors.overduePillText,
  },
});

// Tone -> tint, icon and title color. Semantic colors only; the accent is
// not among them (a warning here is a Waiting-clock amber, danger is the
// overdue red, success the confirmed green tint).
type Tone = { box: object; title: object; icon: typeof RadioTower; iconColor: string };
const NEUTRAL_TONE: Tone = {
  box: styles.boxEmpty,
  title: styles.titleNeutral,
  icon: RadioTower,
  iconColor: colors.textMuted,
};
const TONES: Record<string, Tone> = {
  neutral: NEUTRAL_TONE,
  success: { box: {}, title: styles.titleSuccess, icon: RadioTower, iconColor: colors.accentText },
  warning: {
    box: styles.boxWarning,
    title: styles.titleWarning,
    icon: Clock,
    iconColor: colors.warnPillText,
  },
  danger: {
    box: styles.boxDanger,
    title: styles.titleDanger,
    icon: AlertCircle,
    iconColor: colors.overduePillText,
  },
};
